import React, { useState, useRef } from 'react';
import axios from 'axios';
import { X, Camera, User, Mail, Lock, Save } from 'lucide-react';
import './EditProfileModal.css';
import { mediaUrl } from '../media';

function EditProfileModal({ user, onClose, onUpdate }) {
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const getInitials = (name) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getAvatarUrl = () => {
    if (avatarPreview) return avatarPreview;
    if (user.avatar) return mediaUrl(user.avatar);   // uploads are private (WR-014)
    return null;
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setAvatarPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      return setError('New passwords do not match');
    }
    if (formData.newPassword && formData.newPassword.length < 6) {
      return setError('New password must be at least 6 characters');
    }

    setLoading(true);
    try {
      let response;

      if (avatarFile) {
        // Use multipart only when a file is being uploaded
        const data = new FormData();
        data.append('name', formData.name);
        data.append('email', formData.email);
        if (formData.currentPassword) data.append('currentPassword', formData.currentPassword);
        if (formData.newPassword) data.append('newPassword', formData.newPassword);
        data.append('avatar', avatarFile);
        response = await axios.put('/auth/profile', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Use JSON when no file — simpler and more reliable
        const payload = { name: formData.name, email: formData.email };
        if (formData.currentPassword) payload.currentPassword = formData.currentPassword;
        if (formData.newPassword) payload.newPassword = formData.newPassword;
        response = await axios.put('/auth/profile', payload);
      }

      setSuccess('Profile updated successfully!');
      setFormData(f => ({ ...f, currentPassword: '', newPassword: '', confirmPassword: '' }));
      setTimeout(() => {
        onUpdate(response.data); // update parent state after success message
        onClose();
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const avatarUrl = getAvatarUrl();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-profile-modal" onClick={e => e.stopPropagation()}>
        <div className="epm-header">
          <h2>Edit profile</h2>
          <button className="btn btn-ghost" style={{ padding: '8px', width: '40px', height: '40px' }} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="epm-body">
            {/* Avatar section */}
            <div className="epm-avatar-section">
              <div className="epm-avatar-wrapper" onClick={() => fileInputRef.current?.click()}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar" className="epm-avatar-img" />
                ) : (
                  <div className="avatar epm-avatar-placeholder">
                    {getInitials(formData.name || user.name)}
                  </div>
                )}
                <div className="epm-avatar-overlay">
                  <Camera size={20} />
                  <span>Change</span>
                </div>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />
              <p className="epm-avatar-hint">Click to upload · Max 3MB</p>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Name & email */}
            <div className="epm-section-title"><User size={16} /> Basic Info</div>

            <div className="input-group">
              <label>Full name</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                required
              />
            </div>

            <div className="input-group">
              <label>Email address</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData(f => ({ ...f, email: e.target.value }))}
                required
              />
            </div>

            {/* Password */}
            <div className="epm-section-title"><Lock size={16} /> Change Password <span>(leave blank to keep current)</span></div>

            <div className="input-group">
              <label>Current password</label>
              <input
                type="password"
                value={formData.currentPassword}
                onChange={e => setFormData(f => ({ ...f, currentPassword: e.target.value }))}
                placeholder="Required to change password"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="input-group">
                <label>New password</label>
                <input
                  type="password"
                  value={formData.newPassword}
                  onChange={e => setFormData(f => ({ ...f, newPassword: e.target.value }))}
                  placeholder="Min 6 characters"
                />
              </div>
              <div className="input-group">
                <label>Confirm new password</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={e => setFormData(f => ({ ...f, confirmPassword: e.target.value }))}
                  placeholder="Repeat new password"
                />
              </div>
            </div>
          </div>

          <div className="epm-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <Save size={16} />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProfileModal;
